import React, { useState, useEffect, useRef } from 'react';

/**
 * Mobile Carousel Component for Exclusive Offers
 * Features:
 * - Auto-rotation every 5 seconds
 * - Pause on hover
 * - Touch/swipe gestures
 * - Arrow navigation
 * - Smooth transitions
 */
export default function Carousel({ slides, analytics }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const containerRef = useRef(null);

  // Auto-rotation
  useEffect(() => {
    if (isPaused || slides.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isPaused, slides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  // Touch handlers
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const swipeThreshold = 50;
    const diff = touchStartX.current - touchEndX.current;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        nextSlide();
      } else {
        prevSlide();
      }
    }
  };

  if (!slides || slides.length === 0) {
    return null;
  }

  return (
    <div className="md:hidden py-12 px-4">
      <h2 className="text-2xl font-bold mb-2">Exclusive Member Offers</h2>
      <p className="text-gray-600 mb-6">
        With a Pink Wallet membership, you get exclusive offers from these partners in the LGBTQ+ and ally community.
      </p>

      <div
        ref={containerRef}
        className="relative h-[280px] overflow-hidden rounded-lg"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Slides */}
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${index === currentSlide ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
          >
            {/* Background hero image with gradient overlay */}
            <div className="absolute inset-0">
              <img
                src={slide.heroUrl}
                alt={slide.title}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 from-0% via-black/60 via-30% to-transparent to-50%"></div>
            </div>

            {/* Clickable link overlay */}
            <a
              href={`/businesses/${slide.id}/`}
              className="absolute inset-0 z-10"
              aria-label={`View ${slide.title}`}
              onClick={() => analytics.trackInteraction('carousel_slide_click')}
            />

            {/* Content overlay */}
            <div className="relative h-full flex flex-col items-start justify-end pb-8 text-left pointer-events-none pl-14 pr-14">
              {slide.avatarUrl && (
                <img
                  src={slide.avatarUrl}
                  alt={slide.title}
                  className="w-12 h-12 rounded-full object-cover border-2 border-brand-300 shadow-xl mb-2"
                  loading="lazy"
                />
              )}

              <h2 className="text-xl font-bold text-white mb-1 drop-shadow-lg">
                {slide.title}
              </h2>

              {slide.discount && (
                <p className="text-lg text-white font-semibold drop-shadow-lg">
                  {slide.discount}
                </p>
              )}
            </div>
          </div>
        ))}

        {/* Navigation arrows */}
        {slides.length > 1 && (
          <>
            <button
              onClick={(e) => {
                e.preventDefault();
                prevSlide();
              }}
              className="absolute left-3 top-1/2 -translate-y-1/2 z-20 p-2 flex items-center justify-center hover:opacity-80 transition-opacity"
              aria-label="Previous slide"
            >
              <svg
                className="w-8 h-8 text-white drop-shadow-lg"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.5"
                  d="M15 19l-7-7 7-7"
                ></path>
              </svg>
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                nextSlide();
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 z-20 p-2 flex items-center justify-center hover:opacity-80 transition-opacity"
              aria-label="Next slide"
            >
              <svg
                className="w-8 h-8 text-white drop-shadow-lg"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.5"
                  d="M9 5l7 7-7 7"
                ></path>
              </svg>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
